import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { hashPassword } from './utils/hashPassword';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Link,
} from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';

interface UserData {
  id: string;
  name: string;
  email: string;
  passwordHash: string;
}

const SignUp: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  const handleSignUp = async () => {
    if (!name.trim() || !email.trim() || !password.trim()) {
      alert('すべての項目を入力してください。');
      return;
    }

    const saved = localStorage.getItem('user-list');
    const userList: UserData[] = saved ? JSON.parse(saved) : [];

    if (userList.some(user => user.email === email)) {
      alert('このメールアドレスはすでに登録されています。');
      return;
    }

    const passwordHash = await hashPassword(password);

    const newUser: UserData = {
      id: uuidv4(),
      name,
      email,
      passwordHash,
    };

    const updatedList = [...userList, newUser];
    localStorage.setItem('user-list', JSON.stringify(updatedList));

    alert('登録が完了しました！ログインしてください。');
    navigate('/signin');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh' }}>
      {/* 左: サービス説明 */}
      <Box
        sx={{
          flex: 1,
          backgroundColor: '#f5faff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: 6,
          py: 8,
        }}
      >
        <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
          Magic Scroll
        </Typography>

        <Box mt={2}>
          <Typography variant="h6" gutterBottom>⚙️ 高い適応力</Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            業務に合わせて柔軟に変化し、効率化とシンプルな操作性を提供します。
          </Typography>

          <Typography variant="h6" gutterBottom>🛠️ 長期運用を前提とした設計</Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            ビジネスの将来を支える堅牢な基盤を提供します。
          </Typography>

          <Typography variant="h6" gutterBottom>👍 直感的なUI</Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            だれでもすぐに使えるデザインで、導入教育も最小限に。
          </Typography>

          <Typography variant="h6" gutterBottom>✨ 常に進化する機能</Typography>
          <Typography variant="body1" color="text.secondary">
            現場の声を反映したアップデートで、継続的に進化します。
          </Typography>
        </Box>
      </Box>

      {/* 右: 登録フォーム */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 4,
          py: 6,
        }}
      >
        <Container maxWidth="sm">
          <Card elevation={6} sx={{ borderRadius: 3 }}>
            <Box display="flex" flexDirection="column" alignItems="center" mt={4}>
              <Avatar sx={{ bgcolor: 'primary.main', mb: 1 }}>
                <PersonAddAlt1Icon />
              </Avatar>
              <Typography component="h1" variant="h5" mb={2}>
                新規ユーザー登録
              </Typography>
            </Box>
            <CardContent>
              <TextField
                fullWidth
                label="名前"
                value={name}
                onChange={(e) => setName(e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="メールアドレス"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="パスワード"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
              />

              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSignUp}
                sx={{ mt: 2 }}
              >
                登録する
              </Button>

              <Typography align="center" mt={2}>
                すでにアカウントをお持ちですか？{' '}
                <Link component="button" onClick={() => navigate('/signin')}>ログイン</Link>
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default SignUp;
